import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum InvoiceLineItemKeys {
  itemNumber = 'itemNumber',
  externalItemNumber = 'externalItemNumber',
  description = 'description',
  itemQty = 'itemQty',
  unitPrice = 'unitPrice',
  lineTotal = 'lineTotal',
}

export const invoiceFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  recipient: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  shipTo: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  companyUrl: yup.string().nullable().optional(),
  invoiceNumber: yup.string().nullable().optional(),
  associatedReferenceNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateDue: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  orderNumber: yup.string().nullable().optional(),
  remittanceInstructions: yup.object().shape({
    routingNumber: yup.string().nullable().optional(),
    accountNumber: yup.string().nullable().optional(),
    bankName: yup.string().nullable().optional(),
    bankAddress: yup.string().nullable().optional(),
    beneficiaryName: yup.string().nullable().optional(),
    beneficiaryAddress: yup.string().nullable().optional(),
    paymentLink: yup.string().nullable().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        description: yup.string().nullable().optional(),
        externalItemNumber: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
        lineAmountDue: yup.string().nullable().optional(),
      }),
    )
    .required(),
  shippingRate: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  comments: yup.string().nullable().optional(),
})

export type InvoiceFileData = yup.InferType<typeof invoiceFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<InvoiceFileData>

export const invoiceFileMappings: RequiredFileData = {
  issuer: {
    companyName: '996f039b-0466-4844-ad80-9ea1db887a64',
    addressLine1: '67046b0d-e2a4-486b-bf1e-0bd5910b2791',
    addressLine2: 'b1f4e1c3-3f4a-4e5d-8e2a-2c3d5e6f7a8b',
    city: 'c1d2e3f4-5678-49ab-cdef-0123456789ab',
    state: 'd2e3f4a5-6789-4abc-def0-1234567890bc',
    zipCode: 'e3f4d5c6-7890-4bcd-ef01-2345678901cd',
    country: 'f4b5c6d7-8901-4cde-f012-3456789012de',
    phoneNumber: 'c403c4a5-b72c-446e-8436-debb0479aecf',
    emailAddress: 'd54ab608-80ed-4ab6-a8bf-82f82b8f62e5',
  },
  recipient: {
    companyName: '1bf161fe-0c64-436a-94c2-5883ff56a359',
    addressLine1: '5cf6ab93-14d2-4e06-8a50-2b1d8b4d505b',
    addressLine2: 'a1f4e1c3-2f4a-4e5d-8e2a-2c3d5e6f7a7b',
    city: 'b1d2e3f4-4678-49ab-cdef-0123456789ab',
    state: 'c2e3f4e5-5789-4abc-def0-1234567890bc',
    zipCode: 'd3f4e5b6-6890-4bcd-ef01-2345678901cd',
    country: 'e4d5c6f7-7901-4cde-f012-3456789012de',
    phoneNumber: '7ecaa4ce-1529-4abd-9397-e3978a3f40e8',
    emailAddress: 'f398d58d-96c7-4e9c-bb64-2621154f78d3',
  },
  shipTo: {
    companyName: '2bf262fe-1c64-436a-94c2-5883ff56a459',
    addressLine1: '6cf7ab93-24d2-4e06-8a50-2b1d8b4d515b',
    addressLine2: 'b1f4e1c3-3f4a-4e5d-8e2a-2c3d5e6f7b7b',
    city: 'c1d2e3f4-5678-49ab-cdef-0123456789bb',
    state: 'd2e3f4e5-6789-4abc-def0-1234567890cc',
    zipCode: 'e3f4e5b6-7890-4bcd-ef01-2345678901dd',
    country: 'f4d1a6f7-8901-4cde-f012-3456789012ee',
    phoneNumber: '8ecaa4ce-2529-4abd-9397-e3978a3f50e8',
    emailAddress: 'f398d58d-a6c7-4e9c-bb64-2621154f88d3',
  },
  companyUrl: '1c08d887-8865-4c4e-833b-fc788853a23e',
  invoiceNumber: 'ff44d25d-27ee-4dde-835e-1da6044e6a13',
  associatedReferenceNumber: 'ad093d58-1f4e-412a-a760-2e6b6dd10aa9',
  dateIssued: 'da9726aa-6967-4822-9242-ae398040e2e8',
  dateDue: '09f42123-fa08-4ef6-99c6-afb5055edb9a',
  paymentTerms: '8e3082f3-24b7-4ab3-81aa-448424dd8bdd',
  orderNumber: '96f08d90-397b-4e77-92f3-b51c9aeae8ca',
  remittanceInstructions: {
    routingNumber: 'a890eaa0-0f89-49a0-9a47-54befd403c1f',
    accountNumber: '9f3a6c82-267e-44bf-81da-acc4e6855c7c',
    bankName: 'e2648cea-0854-42f9-b8f1-03e87ea0f662',
    bankAddress: '54058dd7-0f69-4ac0-bd7a-f5a7bfbfa5f5',
    beneficiaryName: '9396809a-f4f8-4113-a463-fb83e0f72d3f',
    beneficiaryAddress: 'a88ad9a0-1a2c-4e4c-a32b-2215e71147b8',
    paymentLink: 'd57227da-4d62-4666-8c1a-9a37c6104685',
  },
  items: [
    {
      itemNumber: null,
      description: '03ae94b0-b408-440f-9e64-bab9e1135c71',
      externalItemNumber: '14d01d34-cad8-4eb5-8c5e-9f0e4c3e5194',
      itemQty: '6d416978-a9fe-4d4c-b672-7c4f0aa3e290',
      unitPrice: '3f577e5e-0ebf-41d5-b96c-47e330121c97',
      lineTotal: null,
      lineAmountDue: '908a3e81-3aa9-42ce-b2dc-cf7d83a2e70c',
    },
  ],
  shippingRate: '816543b3-5576-4704-8ff4-f18fcd623af0',
  agreement: '32245384-37e1-4c72-b28d-c02d13589a26',
  comments: 'af8a731b-29af-41d8-bfa3-c75a52665737',
}
